import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Drawer, DrawerField, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtDate, fmtDateTime } from '@/shared/lib/format'
import { getFieldProgress, getPoll, listVersions, transitionPoll } from './api'
import {
  Pill, categoryLabel, count, methodologyLabel, pct, pollStatusLabel, pollStatusTone,
} from './vocab'

/**
 * One poll, everything about it, and the four places to go next.
 *
 * ## Why the transitions are buttons and not a status dropdown
 *
 * PollSphere accepts `status` as a plain allowlisted PATCH field, so its UI is a
 * dropdown listing every value — including the ones the state machine forbids and
 * the ones that need a reason. A dropdown says "these are equally available".
 * They are not: `→ PAUSED` and `→ CLOSED` demand a written reason, `→ APPROVED`
 * refuses the person who submitted for review, and `→ LIVE` refuses a poll nobody
 * is fielding.
 *
 * So the drawer offers the edges the server allows FROM THIS STATUS, asks for a
 * reason where one is required, and lets the server's refusal be the answer where
 * it cannot know in advance.
 */
export function PollDrawer({ pollId, onClose, onChanged }: {
  pollId: string | null
  onClose: () => void
  onChanged: () => void
}) {
  const canPublish = useCan('survey:publish')
  const canUpdate = useCan('survey:update')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const poll = useLoad(() => (pollId ? getPoll(pollId) : Promise.resolve(null)), [pollId])
  const versions = useLoad(
    () => (pollId ? listVersions(pollId) : Promise.resolve([])), [pollId])
  const progress = useLoad(
    () => (pollId ? getFieldProgress(pollId) : Promise.resolve(null)), [pollId])

  const p = poll.data
  const rates = progress.data?.disposition

  async function move(statusCode: string, needsReason: boolean) {
    if (!pollId) return
    let reason: string | undefined
    if (needsReason) {
      const typed = window.prompt(
        `Why is this poll moving to ${pollStatusLabel(statusCode)}? `
        + 'It is written to the audit log and it is what somebody reads when they ask '
        + 'why fieldwork stopped.')
      if (!typed || !typed.trim()) return
      reason = typed.trim()
    }
    setBusy(true)
    setError(null)
    try {
      await transitionPoll(pollId, statusCode, reason)
      poll.reload()
      onChanged()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  /**
   * The edges come from the SERVER, on the poll.
   *
   * `allowedTransitions` is what `SurveyTransitions` will actually accept from
   * this status. A hardcoded copy of that graph was here, and a copy is wrong in
   * both directions the moment the two disagree: it offers an edge the server
   * refuses, and — the quieter failure — it hides one the server would have
   * allowed, which nobody reports as a bug because there is nothing on screen to
   * report. Only the LABEL and whether a reason is mandatory are decided here,
   * because those are English rather than policy.
   *
   * `PAUSED` and `CLOSED` require a reason: `SurveyTransitions` refuses without
   * one, and the dialog asks for it rather than discovering the refusal.
   */
  const EDGE_COPY: Record<string, { label: string; reason: boolean; publishes: boolean }> = {
    IN_REVIEW: { label: 'Send for review', reason: false, publishes: false },
    DRAFT: { label: 'Return to draft', reason: true, publishes: false },
    APPROVED: { label: 'Approve', reason: false, publishes: true },
    LIVE: { label: p?.statusCode === 'PAUSED' ? 'Resume' : 'Take live', reason: false, publishes: true },
    PAUSED: { label: 'Pause', reason: true, publishes: false },
    CLOSED: { label: 'Close', reason: true, publishes: true },
    ARCHIVED: { label: 'Archive', reason: false, publishes: true },
  }
  const edges: { code: string; label: string; reason: boolean; permitted: boolean }[] =
    (p?.allowedTransitions ?? []).map((code) => {
      const copy = EDGE_COPY[code]
      return {
        code,
        // An edge this build has no wording for is still OFFERED, under its raw
        // code. Dropping it would hide a transition the server just said it
        // would accept.
        label: copy?.label ?? code,
        reason: copy?.reason ?? true,
        permitted: copy?.publishes ? canPublish : canUpdate,
      }
    })

  return (
    <Drawer
      open={!!pollId}
      onClose={onClose}
      width="lg"
      title={p ? p.title : 'Poll'}
      subtitle={p ? (
        <span className="flex items-center gap-2">
          <span className="font-mono text-erp-xs">{p.ref}</span>
          <Pill tone={pollStatusTone(p.statusCode)}>{pollStatusLabel(p.statusCode)}</Pill>
          {p.scopeUnitName && <Pill>{p.scopeUnitName}</Pill>}
          {p.currentVersion && <Pill>{`v${p.currentVersion.versionNo}`}</Pill>}
        </span>
      ) : undefined}
      footer={edges.length > 0 ? (
        <div className="flex items-center gap-2">
          {edges.map((edge) => (
            <button
              key={edge.code}
              type="button"
              className="erp-btn-secondary h-9 text-erp-sm"
              disabled={busy || !edge.permitted}
              // A control the caller might be allowed to use is OFFERED and the
              // server's refusal speaks. It is disabled only where this client
              // already knows the authority is absent — and then it says why.
              title={edge.permitted ? undefined
                : 'Your account does not hold the authority for this transition.'}
              onClick={() => move(edge.code, edge.reason)}
            >
              {edge.label}
              {edge.reason && <span className="ml-1 text-muted-foreground">·  reason</span>}
            </button>
          ))}
        </div>
      ) : undefined}
    >
      <ErrorStrip message={error ?? poll.error} />

      {poll.loading && !p ? <LoadingBlock label="Loading poll…" /> : p && (
        <>
          <DrawerSection title="The poll">
            <DrawerField label="Category" value={categoryLabel(p.categoryCode)} />
            <DrawerField label="Method" value={methodologyLabel(p.methodologyCode)} />
            <DrawerField label="Scope" value={p.scopeUnitName} />
            <DrawerField label="Tracker" value={p.groupName
              ? `${p.groupName}${p.waveNo != null ? ` · wave ${p.waveNo}` : ''}` : null} />
            <DrawerField label="Target sample" value={count(p.targetSample)} />
            <DrawerField label="Confidence" value={`${p.confidenceLevel}%`} />
            <DrawerField label="Window" value={
              p.opensAt || p.closesAt
                ? `${p.opensAt ? fmtDate(p.opensAt) : '—'} → ${p.closesAt ? fmtDate(p.closesAt) : '—'}`
                : null} />
            <DrawerField label="Audio" value={p.audioRecordingMode} />
            <DrawerField label="Consent" value={p.consentRequired ? 'Required' : 'Not required'} />
            <DrawerField label="Offline capture" value={p.allowOffline ? 'Allowed' : 'Online only'} />
            {/*
              The fence, worded as what it does. "SOFT" on a drawer tells a
              reader nothing; "flagged" and "refused" tell them what will happen
              to an interview taken in the wrong place. The radius is shown only
              where it can apply — a unit with a boundary ignores it, and
              printing a number that is not in use is how somebody comes to
              believe a ward is fenced to 300 m.
            */}
            <DrawerField label="Geo-fence" value={
              p.fenceModeCode === 'HARD'
                ? `Refused if outside${p.fenceRadiusM ? ` · ${p.fenceRadiusM} m at a station` : ''}`
                : p.fenceModeCode === 'SOFT'
                  ? `Flagged if outside${p.fenceRadiusM ? ` · ${p.fenceRadiusM} m at a station` : ''}`
                  : 'Off — position recorded, nothing checked'} />
          </DrawerSection>

          <DrawerSection
            title="Fieldwork"
            right={<Link className="text-chocolate hover:underline text-erp-xs"
              to={`/polling/fieldwork?poll=${p.id}`}>Open</Link>}
          >
            {progress.loading ? (
              <DrawerField label="Loading" value="…" />
            ) : progress.data ? (
              <>
                <DrawerField label="Collected"
                  value={`${count(progress.data.collected)} of ${count(p.targetSample)}`} />
                <DrawerField label="Interviewers"
                  value={`${count(progress.data.activeAssignments)} active of ${count(progress.data.assignmentCount)}`} />
                {/*
                  A rate is null, never zero, when its denominator is empty. "0%
                  response rate" and "we have not started" are different
                  statements and the server is careful to distinguish them.
                */}
                <DrawerField label="Response rate (RR1)" value={pct(rates?.responseRate1)} />
                <DrawerField label="Cooperation (COOP1)" value={pct(rates?.cooperationRate1)} />
                <DrawerField label="Refusal (REF1)" value={pct(rates?.refusalRate1)} />
                {rates?.suppressedReason && (
                  <DrawerField label="Why no rates" value={
                    <span className="text-muted-foreground">{rates.suppressedReason}</span>} />
                )}
              </>
            ) : <DrawerField label="Fieldwork" value="—" />}
          </DrawerSection>

          <DrawerSection
            title="Instrument"
            right={<Link className="text-chocolate hover:underline text-erp-xs"
              to={`/polling/builder?poll=${p.id}`}>Open builder</Link>}
          >
            {(versions.data ?? []).length === 0 ? (
              <DrawerField label="Versions" value="—" />
            ) : (versions.data ?? []).map((v) => (
              <DrawerField
                key={v.id}
                label={`v${v.versionNo}`}
                value={
                  <span className="flex items-center gap-2">
                    <Pill>{v.stateCode}</Pill>
                    <span className="text-muted-foreground">
                      {v.questionCount} question{v.questionCount === 1 ? '' : 's'}
                    </span>
                    {v.publishedAt && (
                      <span className="text-muted-foreground">
                        published {fmtDateTime(v.publishedAt)}
                      </span>
                    )}
                  </span>
                }
              />
            ))}
          </DrawerSection>

          <DrawerSection title="Go to">
            <div className="px-4 py-3 flex flex-wrap gap-2">
              <Link className="erp-btn-ghost h-8 text-erp-sm text-chocolate"
                to={`/polling/sample?poll=${p.id}`}>Sample design</Link>
              <Link className="erp-btn-ghost h-8 text-erp-sm text-chocolate"
                to={`/polling/quality?poll=${p.id}`}>Quality queue</Link>
              <Link className="erp-btn-ghost h-8 text-erp-sm text-chocolate"
                to={`/polling/results?poll=${p.id}`}>Results</Link>
              <Link className="erp-btn-ghost h-8 text-erp-sm text-chocolate"
                to={`/polling/exports?poll=${p.id}`}>Exports</Link>
            </div>
          </DrawerSection>

          {p.statusCode === 'DRAFT' && (
            <div className="p-4">
              <Note tone="info">
                Nothing is collected until this poll is live, and it cannot go live until an
                instrument version is published, a consent script exists and at least one
                interviewer is assigned. The server lists whatever is still missing when you
                press the button.
              </Note>
            </div>
          )}
        </>
      )}
    </Drawer>
  )
}
